import { TFunction } from "i18next";
import { mutate } from "swr";

import { capitalize } from "@helpers/general";
import {
    CustomSaveFn,
    PrinterRowData,
    PrinterSettingConfig,
    PRINTER_MODE_OF_SALE_PATH,
} from "@components/Common/PrinterSetting";
import { showSuccessToast } from "@components/Common/SuccessToast";
import { showErrorToast } from "@components/Common/ErrorToast";

const HTTP_PUT = "PUT";
const CONTENT_TYPE_JSON = "application/json";
const PERIPHERAL_SAVE_PATH = "/settings/printer?isPeripheralAssociate=false";
const APP_ID_KIOSK = "KIOSK";
const APP_NAME_EMPTY = "";
const APP_ID_CHILDREN_EMPTY = "";
const PRINTER_NAME_KEY = "Printer name";
const MARK_KEY = "Mark";
const REMARK_KEY = "Remark";
const FONT_SIZE_KEY = "Font size";
const ADD_DATE_EMPTY = "";
const CONFIRM_TITLE_KEY = "Alert";
const CONFIRM_MESSAGE_KEY =
    "Do you want to apply these modifications to all applications that are used immediately?";
const CONFIRM_SECONDARY_MESSAGE_KEY =
    "Attention! If you click on “Validate”, the specific printing setting of the applications will be modified.";

function buildTitle(t: TFunction) {
    return (rowData: PrinterRowData) => {
        const printerName =
            (rowData[t(PRINTER_NAME_KEY)] as string | undefined) ?? "";
        return `${capitalize(t("Print setting"))}-${t(printerName)}`;
    };
}

const peripheralsCustomSave: CustomSaveFn = async ({
    rowData,
    dataBlob,
    userIds,
    fetchGlobal,
    t,
}) => {
    const apiUrl = `${process.env.REACT_APP_API_V2_URL}${PERIPHERAL_SAVE_PATH}`;

    const savedData = {
        userId: userIds.userId,
        shopId: userIds.shopId,
        uuidUser: userIds.uuidUser,
        franchiseId: userIds.franchiseId,
        operatorId: userIds.operatorId,
        appName: APP_NAME_EMPTY,
        appId: APP_ID_KIOSK,
        appId_children: APP_ID_CHILDREN_EMPTY,
        peripheralId: rowData["Id"],
        data: [
            {
                id: rowData["Id"],
                designation: rowData[t(PRINTER_NAME_KEY)],
                mark: rowData[t(MARK_KEY)],
                note: rowData[t(REMARK_KEY)],
                shopId: rowData.shopId,
                type: rowData.type,
                speed: rowData.speed,
                path: rowData.path,
                ip: rowData.ip,
                port: rowData.port,
                font_size: rowData[t(FONT_SIZE_KEY)],
                edit_date: new Date(),
                add_date: ADD_DATE_EMPTY,
                ticket_width: rowData.ticket_width,
                port_series: rowData.port_series,
                isArchived: rowData.isArchived,
                code: rowData.code,
                tickets: rowData.tickets,
                customer_ticket: dataBlob.customer_ticket,
                divers: dataBlob.divers,
                general: dataBlob.general,
                Kitchen_ticket: dataBlob.Kitchen_ticket,
                modelKitchenTicket: dataBlob.modelKitchenTicket,
            },
        ],
    };

    try {
        const data: { error?: unknown } = await fetchGlobal(apiUrl, {
            method: HTTP_PUT,
            headers: { "Content-Type": CONTENT_TYPE_JSON },
            body: JSON.stringify(savedData),
        });
        mutate(apiUrl, data);
        if (data.error) {
            throw new Error("Error while saving peripheral printer settings");
        }
        showSuccessToast(`${t("Registration successfully")!}`);
        return true;
    } catch {
        showErrorToast(`${t("There's an error")}!`);
        return false;
    }
};

export function buildPeripheralsPrinterSettingConfig(
    t: TFunction
): PrinterSettingConfig {
    return {
        appId: "",
        endpoints: {
            save: `${process.env.REACT_APP_API_V2_URL}${PERIPHERAL_SAVE_PATH}`,
            fetchModeOfSale: `${process.env.REACT_APP_API_V2_URL}${PRINTER_MODE_OF_SALE_PATH}`,
        },
        features: {
            confirmBeforeSave: true,
            confirmTitleKey: CONFIRM_TITLE_KEY,
            confirmMessageKey: CONFIRM_MESSAGE_KEY,
            confirmSecondaryMessageKey: CONFIRM_SECONDARY_MESSAGE_KEY,
            includeUuidUser: true,
            includeOperatorId: true,
        },
        titleTemplate: buildTitle(t),
        customSave: peripheralsCustomSave,
    };
}
